import type { IAccountOverview } from "@pulse-ui/codegen/generated_ts/portfolio";
import type { INormalizedPosition } from "@pulse-ui/codegen/generated_ts/portfolio";
import { api } from "../base";

interface OverviewArgs {
  portfolioId: string;
  quoteAsset: string;
  windowMinutes?: number;
}

interface PositionSummaryArgs {
  portfolioId: string;
  quoteAsset: string;
}

const positionEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Fetches account overview data for a portfolio / quote-asset pair. */
    getOverview: build.query<
      Record<string, IAccountOverview | string>,
      OverviewArgs
    >({
      query: ({ portfolioId, quoteAsset, windowMinutes }) => {
        const params: Record<string, string> = {
          portfolio: portfolioId,
          quote_asset: quoteAsset,
        };
        if (windowMinutes && windowMinutes > 0) {
          params.window = String(Math.floor(windowMinutes));
        }
        return { url: "/api/portfolio/v1/overview", params };
      },
      providesTags: (_result, _error, { portfolioId }) => [
        { type: "Overview", id: portfolioId },
      ],
    }),

    /** Fetches position summary for a portfolio / quote-asset pair. */
    getPositionSummary: build.query<
      Record<string, INormalizedPosition | string>,
      PositionSummaryArgs
    >({
      query: ({ portfolioId, quoteAsset }) => ({
        url: "/api/portfolio/v1/position_summary",
        params: { portfolio: portfolioId, quote_asset: quoteAsset },
      }),
      providesTags: (_result, _error, { portfolioId }) => [
        { type: "PositionSummary", id: portfolioId },
      ],
    }),
  }),
});

export const {
  useGetOverviewQuery,
  useLazyGetOverviewQuery,
  useGetPositionSummaryQuery,
  useLazyGetPositionSummaryQuery,
} = positionEndpoints;
